import { Box, Grid, Tabs, Tab } from "@mui/material";
import Header from "../../../components/Header";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezonePlugin from "dayjs/plugin/timezone";
import OtDataGrid, { OT_CLASSES, otClassColumns } from "../../../components/dataGridOt";
import IntelligenceTimeFilter from "../../../components/IntelligenceTimeFilter";
import API from "../../../api";

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

// "OT History" divisa per classe: una tab per classe semantica (il campo `class`
// del documento), colonne rilevanti per classe, conteggi per tab. Il range
// temporale è condiviso da tutte le tab; ricerca/filtri restano per-tab (toolbar).
const OtProtocols = ({ aiView }) => {
  const location = useLocation();
  const timezone = window.sessionStorage.getItem("timezone") || "Etc/GMT";

  const [timeFilter, setTimeFilter] = useState({ dateFrom: null, dateTo: null });
  const [ipFilters, setIpFilters] = useState({ ipSource: "", ipDestination: "" });
  const [activeClass, setActiveClass] = useState(OT_CLASSES[0].key);
  const [counts, setCounts] = useState({});
  const [ready, setReady] = useState(false);

  // Inizializza il range temporale dai query param (stessa logica della vecchia pagina)
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    let dateFrom, dateTo;
    if (queryParams.get("clickedData")) {
      dateFrom = dayjs.tz(queryParams.get("clickedData"), timezone).startOf("hour");
      dateTo = dateFrom.endOf("hour").millisecond(0);
    } else if (queryParams.get("advtimeStamp")) {
      dateFrom = dayjs.tz(queryParams.get("advtimeStamp"), timezone).startOf("day");
      dateTo = dayjs.tz(queryParams.get("advtimeStamp"), timezone).endOf("day").millisecond(0);
    } else if (queryParams.get("dateFrom") && queryParams.get("dateTo")) {
      dateFrom = dayjs.tz(queryParams.get("dateFrom"), timezone);
      dateTo = dayjs.tz(queryParams.get("dateTo"), timezone).endOf("day").millisecond(0);
    } else {
      dateFrom = dayjs().tz(timezone).subtract(1, "hour");
      dateTo = dayjs().tz(timezone);
    }
    setTimeFilter({ dateFrom, dateTo });
    setIpFilters({
      ipSource: queryParams.get("ipSource") || "",
      ipDestination: queryParams.get("ipDestination") || "",
    });
    setReady(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Conteggi per classe (badge tab), aggiornati al variare del range temporale
  useEffect(() => {
    if (!timeFilter.dateFrom || !timeFilter.dateTo) return;
    const ctrl = new AbortController();
    API.getOtClassCounts(
      { timeFilter: [timeFilter.dateFrom.utc().toISOString(), timeFilter.dateTo.utc().toISOString()] },
      ctrl.signal
    )
      .then((res) => {
        const map = {};
        (res.data || []).forEach((c) => { map[c.class] = c.count; });
        setCounts(map);
      })
      .catch(() => { /* abort o errore: badge restano allo stato precedente */ });
    return () => ctrl.abort();
  }, [timeFilter]);

  const passedPayload = {
    dateFrom: timeFilter.dateFrom,
    dateTo: timeFilter.dateTo,
    ipSource: ipFilters.ipSource,
    ipDestination: ipFilters.ipDestination,
  };

  return (
    <Box m="20px">
      <Grid container>
        <Grid size={12} sx={{ display: "flex", justifyContent: "space-between" }}>
          <Header
            sx={{ width: { xs: "40vw", md: "" } }}
            title="Antlia History"
            subtitle=""
            desc="LOGs with low-impact details captured by Antlia across industrial, medical and other protocols are present here, split by event class. Columns adapt to the class. They can be searched, filtered and exported per tab.
                NOTE: Only 4000 records are currently visible. To see more specific records properly use the date to search."
          />
        </Grid>
      </Grid>

      {ready && timeFilter.dateFrom && (
        <>
          <Box mt="10px">
            <IntelligenceTimeFilter
              initialDateFrom={timeFilter.dateFrom}
              initialDateTo={timeFilter.dateTo}
              onDateChange={setTimeFilter}
            />
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: "divider", mt: 2 }}>
            <Tabs
              value={activeClass}
              onChange={(e, v) => setActiveClass(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              {OT_CLASSES.map((c) => (
                <Tab
                  key={c.key}
                  value={c.key}
                  label={`${c.label} (${counts[c.key] ?? 0})`}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                />
              ))}
            </Tabs>
          </Box>

          {/* key={activeClass}: rimonta il grid al cambio tab → filtri/paginazione per-tab */}
          <OtDataGrid
            key={activeClass}
            tipo={3}
            passedPayload={passedPayload}
            hideTimeFilter
            otClass={activeClass}
            columnsOverride={otClassColumns(activeClass)}
            anomalyMode="externalIp"
          />
        </>
      )}
    </Box>
  );
};

export default OtProtocols;
